import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'

/* ─── Variation engine (extracted from V1) ─── */
export function evalCond(when, val) {
  if (!when) return false
  switch (when.operator) {
    case 'eq': return val === when.value
    case 'gt': return Number(val) > Number(when.value)
    case 'gte': return Number(val) >= Number(when.value)
    case 'lt': return Number(val) < Number(when.value)
    case 'lte': return Number(val) <= Number(when.value)
    default: return false
  }
}

export function applyVariations(components, variations, selections) {
  let result = components.map((c, i) => ({ ...c, _i: i, _excluded: false, _mult: 1, _added: false }))
  const added = [], effects = []
  for (const v of (variations || [])) {
    const val = selections[v.attributeId]
    if (val === undefined || val === null) continue
    for (const rule of (v.rules || [])) {
      if (!evalCond(rule.when, val)) continue
      for (const eff of (rule.effects || [])) {
        const tgt = result.find(c => c.description === (eff.targetDescription || '') || c._baseId === (eff.targetId || ''))
        if (eff.action === 'exclude_component' && tgt) { tgt._excluded = true; effects.push({ type: 'exclude', target: tgt.description, note: eff.note, variation: v.label }) }
        if (eff.action === 'include_component' && tgt) { tgt._excluded = false; tgt.isOptional = false; effects.push({ type: 'include', target: tgt.description, note: eff.note, variation: v.label }) }
        if (eff.action === 'multiply_qty' && tgt) { tgt._mult *= (eff.value || 1); effects.push({ type: 'modify', target: tgt.description, note: eff.note, variation: v.label }) }
        if (eff.action === 'add_component' && eff.value) { added.push({ ...eff.value, _excluded: false, _mult: 1, _added: true, _by: v.label }); effects.push({ type: 'add', target: eff.value.description, note: eff.note, variation: v.label }) }
        if (eff.action === 'formula_qty' && tgt && eff.formula) {
          tgt._formulaQty = eff.formula
          effects.push({ type: 'modify', target: tgt.description, note: eff.note, variation: v.label })
        }
      }
    }
  }
  return { components: [...result, ...added], effects }
}

/* ─── Client-side formula evaluator (extracted from V1) ─── */
export function clientEvalFormula(str, vars) {
  if (!str) return 0
  try {
    const tokens = []
    let i = 0
    while (i < str.length) {
      const ch = str[i]
      if (/\s/.test(ch)) { i++; continue }
      if ('+-*/()'.includes(ch)) { tokens.push({ t: ch === '(' ? 'L' : ch === ')' ? 'R' : 'O', v: ch }); i++; continue }
      if (/[0-9.]/.test(ch)) { let n = ''; while (i < str.length && /[0-9.]/.test(str[i])) n += str[i++]; tokens.push({ t: 'N', v: parseFloat(n) || 0 }); continue }
      if (/[a-zA-Z_]/.test(ch)) { let nm = ''; while (i < str.length && /[a-zA-Z0-9_]/.test(str[i])) nm += str[i++]; tokens.push({ t: 'V', v: nm }); continue }
      i++
    }
    let pos = 0
    const peek = () => pos < tokens.length ? tokens[pos] : null
    const eat = () => tokens[pos++]
    function parseExpr() {
      let l = parseTerm()
      while (peek() && peek().t === 'O' && (peek().v === '+' || peek().v === '-')) { const op = eat().v; const r = parseTerm(); l = op === '+' ? l + r : l - r }
      return l
    }
    function parseTerm() {
      let l = parseFact()
      while (peek() && peek().t === 'O' && (peek().v === '*' || peek().v === '/')) { const op = eat().v; const r = parseFact(); l = op === '/' ? (r === 0 ? 0 : l / r) : l * r }
      return l
    }
    function parseFact() {
      const tk = peek()
      if (!tk) return 0
      if (tk.t === 'O' && tk.v === '-') { eat(); return -parseFact() }
      if (tk.t === 'L') { eat(); const v = parseExpr(); if (peek() && peek().t === 'R') eat(); return v }
      if (tk.t === 'N') { eat(); return tk.v }
      if (tk.t === 'V') {
        eat()
        const fn = tk.v.toLowerCase()
        if ((fn === 'ceil' || fn === 'floor' || fn === 'max' || fn === 'min') && peek() && peek().t === 'L') {
          eat()
          const args = [parseExpr()]
          while (peek() && peek().t === 'O' && peek().v === ',') { eat(); args.push(parseExpr()) }
          if (peek() && peek().t === 'R') eat()
          if (fn === 'ceil') return Math.ceil(args[0])
          if (fn === 'floor') return Math.floor(args[0])
          if (fn === 'max') return Math.max(...args)
          if (fn === 'min') return Math.min(...args)
        }
        const val = vars[tk.v]
        return typeof val === 'number' ? val : 0
      }
      eat(); return 0
    }
    const result = parseExpr()
    return isFinite(result) ? result : 0
  } catch { return 0 }
}

/* ─── Effect action metadata ─── */
export const EFFECT_ACTIONS = [
  { key: 'exclude_component', label: 'Exclude Component', desc: 'Remove a component from the recipe', icon: '\u2715', tagType: 'exclude' },
  { key: 'include_component', label: 'Include Component', desc: 'Force-include an optional component', icon: '\u2713', tagType: 'include' },
  { key: 'multiply_qty', label: 'Scale Quantity', desc: "Scale a component's hours or qty", icon: '\u00D7', tagType: 'modify' },
  { key: 'add_component', label: 'Add Component', desc: 'Add a new component to the recipe', icon: '+', tagType: 'add' },
  { key: 'formula_qty', label: 'Compute Quantity', desc: 'Compute qty from formula expression', icon: '\u21BB', tagType: 'compute' }
]

export const COST_TYPES = ['labor', 'equipment', 'material', 'subcontract', 'disposal']

export const COST_TYPE_META = {
  labor: { label: 'LABOR', chipClass: 'chip-labor' },
  equipment: { label: 'EQUIPMENT', chipClass: 'chip-equipment' },
  material: { label: 'MATERIAL', chipClass: 'chip-material' },
  subcontract: { label: 'SUBCONTRACT', chipClass: 'chip-subcontract' },
  disposal: { label: 'DISPOSAL', chipClass: 'chip-disposal' }
}

export const CAT_COLORS = {
  'Chemical Applications': '#8b5cf6', 'Herbicide & Chemical': '#8b5cf6',
  'Demo': '#ef4444', 'Earthwork': '#d97706', 'Irrigation': '#3b82f6',
  'Planting': '#10b981', 'Turf': '#84cc16', 'Hardscape': '#6366f1',
  'Lighting': '#f59e0b', 'Drainage': '#06b6d4', 'General': '#6b7280',
  'Water Feature': '#0ea5e9', 'Erosion Control': '#a3e635'
}

export const FALLBACK_UNITS = ['SF', 'LF', 'EA', 'CY', 'LS', 'AC', 'GAL', 'TON', 'HR']

/* ─── Formatting helpers ─── */
export const fmt = (n) => n != null && n > 0 ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '\u2014'
export const fmtHrs = (n) => n != null && n > 0 ? `${n.toFixed(1)} hrs` : '\u2014'

/* ─── Live Preview calculation (extracted from V1) ─── */
export function calcPreview(components, inputSlots, slotValues, laborRates, equipmentRates, productionRates, minimumTotal, classBreakdowns, defaultMaterials) {
  const slotsByUnit = {}
  for (const slot of (inputSlots || [])) {
    const val = slotValues[slot.key] ?? slot.defaultValue ?? 0
    if (!slotsByUnit[slot.unit]) slotsByUnit[slot.unit] = { value: val, slot }
    else if (val > 0 && !slotsByUnit[slot.unit].value) slotsByUnit[slot.unit] = { value: val, slot }
  }
  const primarySlot = (inputSlots || []).find(s => (slotValues[s.key] ?? s.defaultValue ?? 0) > 0) || (inputSlots || [])[0]
  const primaryQty = primarySlot ? (slotValues[primarySlot.key] ?? primarySlot.defaultValue ?? 0) : 0

  const findProdRate = (comp) => {
    if (comp.productionRateId) {
      const linked = productionRates.find(p => p._id === comp.productionRateId)
      if (linked) return linked
    }
    const primaryUnit = primarySlot ? primarySlot.unit : null
    if (primaryUnit) {
      const byRoleAndUnit = productionRates.filter(p => p.role === comp.laborRole && p.unit === primaryUnit)
      if (byRoleAndUnit.length === 1) return byRoleAndUnit[0]
      if (byRoleAndUnit.length > 1) {
        const desc = (comp.description || '').toLowerCase()
        const descMatch = byRoleAndUnit.find(p => {
          const prDesc = (p.description || '').toLowerCase()
          return prDesc.includes(desc) || desc.includes(prDesc.split('(')[0].trim().toLowerCase())
        })
        if (descMatch) return descMatch
        return byRoleAndUnit[0]
      }
    }
    const byRole = productionRates.filter(p => p.role === comp.laborRole)
    for (const pr of byRole) {
      if (slotsByUnit[pr.unit] && slotsByUnit[pr.unit].value > 0) return pr
    }
    return null
  }

  const getQtyForRate = (pr) => {
    if (!pr) return primaryQty
    const match = slotsByUnit[pr.unit]
    return match ? match.value : primaryQty
  }

  const rows = []
  let totalLaborHrs = 0, totalLaborCost = 0, totalEquipHrs = 0, totalEquipCost = 0
  let totalMaterialCost = 0, totalSubCost = 0, totalDisposalCost = 0

  for (const c of components) {
    if (c._excluded) { rows.push({ ...c, hours: 0, cost: 0, rate: null, prodRate: null }); continue }
    const row = { ...c, hours: 0, cost: 0, rate: null, prodRate: null }

    if (c.costType === 'labor') {
      if (c.followsLeadHours) {
        const lr = laborRates.find(l => l.role === c.laborRole)
        row.rate = lr ? lr.hourlyRate : 0
        row._followsLead = true
        rows.push(row)
        continue
      }
      const pr = findProdRate(c)
      const lr = laborRates.find(l => l.role === c.laborRole)
      row.rate = lr ? lr.hourlyRate : 0
      if (pr) {
        const qty = getQtyForRate(pr)
        if (qty > 0) {
          row.prodRate = pr
          const baseHrs = qty / pr.baseRate
          row.hours = Math.max(baseHrs * (c._mult || 1), c.minimumHours || 0)
        } else { row.hours = c.minimumHours || 0 }
      } else if (c.quantityMode === 'manual' && c.defaultQty > 0) {
        row.hours = Math.max(c.defaultQty * (c._mult || 1), c.minimumHours || 0)
      } else { row.hours = c.minimumHours || 0 }
      row.cost = row.hours * row.rate
      totalLaborHrs += row.hours; totalLaborCost += row.cost
    } else if (c.costType === 'equipment') {
      const er = equipmentRates.find(e => e.name === c.equipmentRole || e.name === c.description)
      row.rate = er ? (er.hourlyRate || er.rate || 0) : 0
      row.hours = c.minimumHours || 0
      row.cost = row.hours * row.rate
      totalEquipHrs += row.hours; totalEquipCost += row.cost
    } else if (c.costType === 'material') {
      row.cost = (c.defaultUnitCost || 0) * (c.defaultQty || primaryQty || 0)
      totalMaterialCost += row.cost
    } else if (c.costType === 'subcontract') {
      row.cost = c.defaultUnitCost || 0
      totalSubCost += row.cost
    } else if (c.costType === 'disposal') {
      row.cost = c.defaultUnitCost || 0
      totalDisposalCost += row.cost
    }
    rows.push(row)
  }

  // Second pass: follows-lead
  const lead = rows.find(r => r.costType === 'labor' && !r._followsLead && !r._excluded && r.hours > 0)
  const leadHours = lead ? lead.hours : 0
  for (const row of rows) {
    if (row._followsLead && !row._excluded && leadHours > 0) {
      row.hours = Math.max(leadHours, row.minimumHours || 0)
      row.cost = row.hours * (row.rate || 0)
      totalLaborHrs += row.hours; totalLaborCost += row.cost
    }
  }
  for (const row of rows) {
    if (row.costType === 'equipment' && row.followsLeadHours && !row._excluded && leadHours > 0) {
      const prevHrs = row.hours; const prevCost = row.cost
      row.hours = Math.max(leadHours, row.minimumHours || 0)
      row.cost = row.hours * (row.rate || 0)
      totalEquipHrs += row.hours - prevHrs; totalEquipCost += row.cost - prevCost
    }
  }

  // Size class resolution
  const classDetails = []
  let sizeClassHours = 0
  for (const slot of (inputSlots || [])) {
    if (!slot.isSizeClassInput || !(slot.quantityClasses || []).length) continue
    const bk = (classBreakdowns || {})[slot.key] || {}
    for (const cls of slot.quantityClasses) {
      const qty = Number(bk[cls.classKey]) || 0
      if (qty <= 0) continue
      const pr = cls.productionRateId ? productionRates.find(p => p._id === cls.productionRateId) : null
      const hrs = pr && pr.baseRate > 0 ? qty / pr.baseRate : 0
      classDetails.push({ classKey: cls.classKey, label: cls.label, qty, prodRate: pr, hours: hrs, slotKey: slot.key })
      sizeClassHours += hrs
    }
  }
  if (sizeClassHours > 0) {
    const leadRow = rows.find(r => r.costType === 'labor' && !r._followsLead && !r._excluded)
    if (leadRow) {
      totalLaborHrs -= leadRow.hours; totalLaborCost -= leadRow.cost
      leadRow.hours = sizeClassHours; leadRow.cost = sizeClassHours * (leadRow.rate || 0)
      totalLaborHrs += leadRow.hours; totalLaborCost += leadRow.cost
    }
    for (const row of rows) {
      if (row._followsLead && !row._excluded) {
        totalLaborHrs -= row.hours; totalLaborCost -= row.cost
        row.hours = Math.max(sizeClassHours, row.minimumHours || 0)
        row.cost = row.hours * (row.rate || 0)
        totalLaborHrs += row.hours; totalLaborCost += row.cost
      }
      if (row.costType === 'equipment' && row.followsLeadHours && !row._excluded) {
        totalEquipHrs -= row.hours; totalEquipCost -= row.cost
        row.hours = Math.max(sizeClassHours, row.minimumHours || 0)
        row.cost = row.hours * (row.rate || 0)
        totalEquipHrs += row.hours; totalEquipCost += row.cost
      }
    }
  }

  // Aggregate components (second pass)
  const nonAggTotal = totalLaborCost + totalEquipCost + totalMaterialCost + totalSubCost + totalDisposalCost
  for (const row of rows) {
    if (row.quantityMode === 'aggregate' && !row._excluded) {
      const pct = (row.aggregatePct || 0) / 100
      const target = row.aggregateOf || 'all'
      let base = nonAggTotal
      if (target === 'labor') base = totalLaborCost
      else if (target === 'material') base = totalMaterialCost
      else if (target === 'equipment') base = totalEquipCost
      else if (target === 'subcontract') base = totalSubCost
      row.cost = base * pct
      row._aggregateOf = target
    }
  }

  // Material rule resolution
  const materialLines = []
  for (const mat of (defaultMaterials || [])) {
    let computedQty = 0
    if (mat.formula) {
      computedQty = clientEvalFormula(mat.formula, slotValues || {})
    } else if (mat.perClass) {
      for (const slot of (inputSlots || [])) {
        if (!slot.isSizeClassInput) continue
        const bk = (classBreakdowns || {})[slot.key] || {}
        for (const cls of (slot.quantityClasses || [])) {
          if (mat.classFilter && mat.classFilter.length > 0 && !mat.classFilter.includes(cls.classKey)) continue
          computedQty += (Number(bk[cls.classKey]) || 0) * (mat.qtyPerUnit || 1)
        }
      }
    } else if (mat.inputSlotKey) {
      computedQty = ((slotValues || {})[mat.inputSlotKey] || 0) * (mat.qtyPerUnit || 1)
    }
    materialLines.push({ label: mat.label, unit: mat.unit || '', computedQty, formula: mat.formula })
  }

  const aggTotal = rows.filter(r => r.quantityMode === 'aggregate' && !r._excluded).reduce((s, r) => s + r.cost, 0)
  const grandTotal = totalLaborCost + totalEquipCost + totalMaterialCost + totalSubCost + totalDisposalCost + aggTotal
  const effectiveTotal = minimumTotal ? Math.max(grandTotal, minimumTotal) : grandTotal

  return { rows, totalLaborHrs, totalLaborCost, totalEquipHrs, totalEquipCost, totalMaterialCost, totalSubCost, totalDisposalCost, grandTotal, effectiveTotal, classDetails, materialLines }
}

/* ═══════════════════════════════════════════════════════════
   useRecipeState — Main state hook for RecipeBuilderV2
   ═══════════════════════════════════════════════════════════ */
export default function useRecipeState() {
  const url = useSelector(state => state.url)

  const [services, setServices] = useState([])
  const [templates, setTemplates] = useState([])
  const [laborRates, setLaborRates] = useState([])
  const [equipmentRates, setEquipmentRates] = useState([])
  const [productionRates, setProductionRates] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Editor state
  const [editIdx, setEditIdx] = useState(null)
  const [comps, setComps] = useState([])
  const [vars, setVars] = useState([])
  const [inputSlots, setInputSlots] = useState([])
  const [defaultMaterials, setDefaultMaterials] = useState([])
  const [childRecipes, setChildRecipes] = useState([])
  const [minimumTotal, setMinimumTotal] = useState(null)
  const [dirty, setDirty] = useState(false)

  // Preview state
  const [pvSel, setPvSel] = useState({})
  const [slotValues, setSlotValues] = useState({})
  const [classBreakdowns, setClassBreakdowns] = useState({})

  // List state
  const [search, setSearch] = useState('')
  const [catFilter, setCatFilter] = useState('All')
  const [section, setSection] = useState('components')

  const fetchAll = useCallback(() => {
    setLoading(true)
    Promise.all([
      axios.get(`${url}/api/bid-estimation/services?includeInactive=true`),
      axios.get(`${url}/api/bid-estimation/service-templates`),
      axios.get(`${url}/api/bid-estimation/labor-rates`),
      axios.get(`${url}/api/bid-estimation/equipment-rates`),
      axios.get(`${url}/api/bid-estimation/production-rates`)
    ]).then(([s, t, l, e, p]) => {
      setServices(s.data || []); setTemplates(t.data || [])
      setLaborRates(l.data || []); setEquipmentRates(e.data || [])
      setProductionRates(p.data || []); setLoading(false)
    }).catch(() => setLoading(false))
  }, [url])

  useEffect(() => { fetchAll() }, [fetchAll])

  const tMap = useMemo(() => {
    const m = {}; for (const t of templates) m[t.serviceId] = t; return m
  }, [templates])

  const serviceSearchText = useCallback((service) => [
    service?.name,
    service?.serviceName,
    service?.serviceId,
    service?.category,
    service?.serviceCategory,
    ...(service?.aliases || []),
    ...(tMap[service?.serviceId]?.aliases || [])
  ].filter(Boolean).join(' ').toLowerCase(), [tMap])

  const allWithRecipes = useMemo(() => services.filter(s => !s.entity ? !!tMap[s.serviceId] : true), [services, tMap])
  const categories = useMemo(() => ['All', ...new Set(allWithRecipes.map(s => s.category))].sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b)), [allWithRecipes])

  const svcList = useMemo(() => {
    let list = allWithRecipes
    if (catFilter !== 'All') list = list.filter(s => s.category === catFilter)
    if (search) { const q = search.toLowerCase(); list = list.filter(s => serviceSearchText(s).includes(q)) }
    return list
  }, [allWithRecipes, catFilter, search, serviceSearchText])

  const svc = editIdx !== null ? svcList[editIdx] : null
  const tmpl = svc ? tMap[svc.serviceId] : null

  // Load template data when opening editor
  useEffect(() => {
    if (editIdx !== null && tmpl) {
      setComps(JSON.parse(JSON.stringify(tmpl.components || [])))
      setVars(JSON.parse(JSON.stringify(tmpl.variations || [])))
      setInputSlots(JSON.parse(JSON.stringify(tmpl.inputSlots || [])))
      setDefaultMaterials(JSON.parse(JSON.stringify(tmpl.defaultMaterials || [])))
      setChildRecipes(JSON.parse(JSON.stringify(tmpl.childRecipes || [])))
      setMinimumTotal(tmpl.minimumTotal || null)
      const s = {}; for (const v of (tmpl.variations || [])) s[v.attributeId] = v.defaultValue; setPvSel(s)
      const sv = {}; for (const slot of (tmpl.inputSlots || [])) { if (slot.defaultValue != null) sv[slot.key] = slot.defaultValue }; setSlotValues(sv)
      setClassBreakdowns({})
    } else if (editIdx !== null) {
      setComps([]); setVars([]); setInputSlots([]); setDefaultMaterials([]); setChildRecipes([]); setPvSel({}); setSlotValues({}); setMinimumTotal(null); setClassBreakdowns({})
    }
    setDirty(false)
  }, [tmpl?._id, editIdx])

  const preview = useMemo(() => applyVariations(comps, vars, pvSel), [comps, vars, pvSel])

  const previewCalc = useMemo(() =>
    calcPreview(preview.components, inputSlots, slotValues, laborRates, equipmentRates, productionRates, minimumTotal, classBreakdowns, defaultMaterials),
    [preview.components, inputSlots, slotValues, laborRates, equipmentRates, productionRates, minimumTotal, classBreakdowns, defaultMaterials]
  )

  const save = useCallback(async () => {
    if (!svc) return
    setSaving(true)
    try {
      await axios.post(`${url}/api/bid-estimation/service-templates`, {
        serviceId: svc.serviceId, serviceName: svc.name, serviceCategory: svc.category,
        components: comps.filter(c => c.description || c.costType),
        variations: vars,
        inputSlots: inputSlots.filter(s => s.key && s.label),
        defaultMaterials: defaultMaterials.filter(m => m.label || m.inputSlotKey || m.formula),
        childRecipes: childRecipes.filter(cr => cr.childServiceId),
        minimumTotal: minimumTotal || undefined
      })
      setSaving(false); setDirty(false); fetchAll()
    } catch (e) { setSaving(false); console.error(e) }
  }, [svc, url, comps, vars, inputSlots, defaultMaterials, childRecipes, minimumTotal, fetchAll])

  // Mark dirty on any edit
  const markDirty = useCallback(() => setDirty(true), [])

  return {
    // Data
    services, templates, laborRates, equipmentRates, productionRates,
    loading, saving, url,
    // Template map
    tMap, allWithRecipes, categories, svcList,
    // List state
    search, setSearch, catFilter, setCatFilter,
    // Editor state
    editIdx, setEditIdx, section, setSection,
    svc, tmpl, dirty, setDirty, markDirty,
    comps, setComps, vars, setVars,
    inputSlots, setInputSlots,
    defaultMaterials, setDefaultMaterials,
    childRecipes, setChildRecipes,
    minimumTotal, setMinimumTotal,
    // Preview state
    pvSel, setPvSel, slotValues, setSlotValues,
    classBreakdowns, setClassBreakdowns,
    preview, previewCalc,
    // Actions
    save, fetchAll
  }
}
